/**
 * Shared Tailwind form / selectable-box tokens.
 * Single source of truth: gray border default, accent focus/selected, error after validation.
 *
 * Size hierarchy (see fonts.css / typeShell):
 * - form label / fields: text-sm on iPad → bp-2xl:text-base
 * - formSubsectionTitle: text-lg (form subsection, below pageShell.sectionTitle)
 * - hint / error: text-sm floor
 * - Primary page CTAs use Button size="cta" (buttonShell)
 */

import {
    controlHeight,
    fieldText,
    fieldTextControl,
    fieldTextMuted,
    typeShell,
} from "./typeShell";

const fieldChrome =
    "box-border w-full rounded-lg border-2 border-solid bg-surface px-3 shadow-none transition-[border-color,box-shadow,background-color] placeholder:text-text-hint focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-accent-15 disabled:cursor-not-allowed disabled:border-border disabled:bg-surface-muted disabled:text-text-muted disabled:opacity-100";

/**
 * Fixed-height controls (input / select): py-0 + fieldTextControl so glyphs fit in h-11.
 * py-3 + fieldText leading-normal otherwise clips inside max-h-11 + border-2.
 */
const controlFieldBase = `${fieldChrome} py-0 ${fieldTextControl} ${controlHeight}`;

/** Auto-height fields (textarea): keep vertical padding + normal leading */
const paddedFieldBase = `${fieldChrome} py-3 ${fieldText}`;

/** Fixed / non-editable value: muted fill, no accent focus */
const readonlyBase = `box-border w-full cursor-default rounded-lg border border-solid border-border bg-surface-muted px-3 py-3 ${fieldTextMuted} shadow-none outline-none focus:border-border focus:ring-0 focus:outline-none`;

const choiceBase =
    "box-border cursor-pointer rounded-lg border border-solid bg-surface shadow-none transition-[border-color,box-shadow,background-color] focus-within:outline-none";

const dropzoneBase = `box-border flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-border bg-surface px-3 py-3 text-center ${fieldText} transition-[border-color,box-shadow,background-color] hover:border-accent hover:bg-accent-10 focus-within:border-accent focus-within:ring-[3px] focus-within:ring-accent-15`;

/** Native select chevron: slightly inset from the right edge */
const selectChevron =
    "appearance-none bg-[length:0.75rem_0.75rem] bg-[right_0.875rem_center] bg-no-repeat !pr-10 bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23495057' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")]";

const toggleTrack =
    "pointer-events-none absolute inset-0 block rounded-full bg-border transition-colors peer-checked:bg-button-primary peer-disabled:bg-ui-muted";
const toggleThumb =
    "pointer-events-none absolute left-[3px] top-[3px] block h-5 w-5 rounded-full bg-text-primary transition-transform peer-checked:translate-x-[24px] peer-disabled:bg-text-hint";

export const formShell = {
    form: "flex w-full flex-col gap-section",
    layout2col: "grid w-full grid-cols-1 gap-section bp-md:grid-cols-2",
    grid2: "grid w-full grid-cols-1 gap-section bp-md:grid-cols-2",
    grid3: "grid w-full grid-cols-1 gap-section bp-md:grid-cols-3",
    gridAuto:
        "grid w-full grid-cols-1 gap-section bp-md:grid-cols-[repeat(auto-fill,minmax(15.625rem,1fr))]",
    gridAi: "grid w-full grid-cols-1 gap-section bp-md:grid-cols-3 bp-lg:grid-cols-4",
    fullWidth: "col-span-full w-full min-w-0",
    section: "flex w-full flex-col gap-3",
    formSubsectionTitle: `m-0 ${typeShell.formSubsectionTitle}`,
    group: "flex min-w-0 flex-col gap-2",
    label: `m-0 ${typeShell.fieldLabel}`,
    input: `${controlFieldBase} border-border`,
    inputError: `${controlFieldBase} border-error focus:border-error focus:ring-error-15`,
    numberInput:
        "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
    textarea: `${paddedFieldBase} min-h-20 resize-y border-border`,
    select: `${controlFieldBase} ${selectChevron} cursor-pointer border-border`,
    selectError: `${controlFieldBase} ${selectChevron} cursor-pointer border-error focus:border-error focus:ring-error-15`,
    readonlyValue: readonlyBase,
    error: `m-0 ${typeShell.error}`,
    hint: `m-0 ${typeShell.hint}`,
    togglePanel: `box-border flex ${controlHeight} min-w-0 w-full items-center rounded-lg border-2 border-solid border-border bg-surface px-3 py-0`,
    toggleRow: "inline-flex w-fit max-w-full cursor-pointer select-none items-center gap-3",
    toggleRowDisabled:
        "inline-flex w-fit max-w-full cursor-not-allowed select-none items-center gap-3 opacity-60",
    toggleSwitchWrap: "relative inline-block h-[26px] w-[50px] shrink-0",
    toggleInput: "peer sr-only",
    toggleTrack,
    toggleThumb,
    toggleLabel: typeShell.bodyMedium,
    toggleLabelDisabled: "text-sm font-medium leading-normal text-text-hint bp-2xl:text-base",
    labelRow: `box-border flex ${controlHeight} min-w-0 w-full items-center justify-between gap-3 rounded-lg border-2 border-solid border-border bg-surface px-3 py-0`,
    labelRowReadonly: `box-border flex ${controlHeight} min-w-0 w-full cursor-default items-center justify-between gap-3 rounded-lg border-2 border-solid border-border bg-surface-muted px-3 py-0`,
    labelRowInner: "flex min-w-0 flex-1 items-center gap-3",
    colorSwatch: "h-5 w-5 shrink-0 rounded-full border-2 border-solid border-black/10",
    labelName: `truncate ${fieldText}`,
    labelNameReadonly: `truncate ${fieldTextMuted}`,
    labelMenuBtn:
        "inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent p-1 text-text-muted transition-colors hover:bg-surface-muted hover:text-text-primary",
    labelMenuDropdown:
        "absolute right-0 top-full z-10 mt-1 min-w-[8.75rem] overflow-hidden rounded-lg border border-solid border-border bg-surface shadow-lg",
    labelMenuItem:
        "flex w-full cursor-pointer items-center gap-2 border-none bg-transparent px-3 py-2 text-left text-sm text-text-primary transition-colors hover:bg-surface-muted",
    labelMenuItemDanger: "text-error hover:bg-error-10",
    formRow: "flex w-full flex-col gap-3 bp-md:flex-row bp-md:items-end",
    colorPicker: `${controlHeight} w-12 shrink-0 cursor-pointer overflow-hidden rounded-lg border-none bg-transparent p-0 transition-transform hover:scale-105 bp-md:w-16`,
    urlRow: "flex w-full items-stretch gap-2",
    urlInput: `${readonlyBase} min-w-0 flex-1 font-mono disabled:cursor-not-allowed disabled:opacity-100`,

    /* FormActions */
    actions:
        "box-border flex w-full flex-col items-stretch justify-end gap-2 border-0 border-t border-solid border-border pt-section bp-lg:flex-row bp-lg:items-center bp-lg:gap-4",
    actionsBleed:
        "box-border flex w-full flex-col items-stretch justify-end gap-2 border-0 border-t border-solid border-border pt-section bp-lg:flex-row bp-lg:items-center bp-lg:gap-4",
    actionsInline:
        "box-border mt-0 flex w-full flex-row items-center justify-between gap-2 border-0 border-t-0 border-solid border-transparent pt-0",
    actionsUnsaved:
        "pointer-events-none mr-auto flex cursor-default select-none items-center gap-1.5 rounded-lg border border-solid border-warning bg-warning-10 px-3 py-1.5 text-sm font-medium text-warning bp-lg:gap-2 bp-lg:px-4 bp-lg:py-2",

    /* Selectable boxes (chips, filter rows) */
    choiceBox: `${choiceBase} border-border hover:border-accent focus-within:border-accent focus-within:ring-[3px] focus-within:ring-accent-15`,
    choiceBoxSelected: `${choiceBase} border-accent bg-accent-10 ring-[3px] ring-accent-15`,
    choiceBoxError: `${choiceBase} border-error focus-within:border-error focus-within:ring-[3px] focus-within:ring-error-15`,

    /* File dropzones / file-input labels */
    dropzone: dropzoneBase,
    dropzoneActive: `${dropzoneBase} border-accent bg-accent-10 ring-[3px] ring-accent-15`,
    dropzoneDisabled: `${dropzoneBase} cursor-not-allowed opacity-60 hover:border-border hover:bg-surface`,
};

/**
 * @param {boolean} hasError
 * @param {"input"|"select"|"textarea"} [kind]
 * @param {boolean} [isNumber]
 */
export function formFieldClass(hasError, kind = "input", isNumber = false) {
    const base =
        kind === "textarea"
            ? hasError
                ? formShell.inputError
                : formShell.textarea
            : hasError
                ? kind === "select"
                    ? formShell.selectError
                    : formShell.inputError
                : kind === "select"
                    ? formShell.select
                    : formShell.input;
    return isNumber ? `${base} ${formShell.numberInput}` : base;
}

/**
 * @param {boolean} selected
 * @param {boolean} [hasError]
 */
export function choiceBoxClass(selected, hasError = false) {
    if (hasError) return formShell.choiceBoxError;
    return selected ? formShell.choiceBoxSelected : formShell.choiceBox;
}
